import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { toast } from 'react-toastify'

interface Article {
  id: string
  title: string
  content: string
  excerpt: string
  slug: string
  views: number
  helpful: number
  notHelpful: number
  tags: string[]
  publishedAt: string
  updatedAt: string
  category: {
    id: string
    name: string
    slug: string
    icon?: string
  } | null
  author: {
    id: string
    name: string
  }
}

export const ArticleDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hasVoted, setHasVoted] = useState(false)

  useEffect(() => {
    if (slug) {
      fetchArticle()
    }
  }, [slug])

  const fetchArticle = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await api.get(`/articles/${slug}`)
      setArticle(response.data.data)
    } catch (error: any) {
      setError('Failed to load article')
      toast.error('Article not found')
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (helpful: boolean) => {
    if (hasVoted || !article) return

    try {
      await api.post(`/articles/${article.id}/helpful`, { helpful })
      setHasVoted(true)
      toast.success('Thank you for your feedback!')
      
      // Update local count
      setArticle({
        ...article,
        [helpful ? 'helpful' : 'notHelpful']: article[helpful ? 'helpful' : 'notHelpful'] + 1
      })
    } catch (error) {
      toast.error('Failed to submit feedback')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-3">Loading article...</p>
        </div>
      </Container>
    )
  }

  if (error || !article) {
    return (
      <Container fluid className="py-5">
        <Row>
          <Col lg={8} className="mx-auto">
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error || 'Article not found'}
            </Alert>
            <Button variant="outline-primary" onClick={() => navigate('/articles')}>
              <i className="fas fa-arrow-left me-2"></i>
              Back to Articles
            </Button>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col lg={8} xl={9}>
          {/* Article Header */}
          <div className="mb-4">
            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-3"
              onClick={() => navigate('/articles')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Articles
            </Button>

            {article.category && (
              <div className="mb-2">
                <Badge bg="primary">
                  {article.category.icon && (
                    <i className={`${article.category.icon} me-1`}></i>
                  )}
                  {article.category.name}
                </Badge>
              </div>
            )}

            <h1 className="mb-3">{article.title}</h1>

            <div className="d-flex align-items-center gap-3 text-muted">
              <span>
                <i className="fas fa-user me-1"></i>
                {article.author.name}
              </span>
              <span>
                <i className="fas fa-calendar me-1"></i>
                {formatDate(article.publishedAt)}
              </span>
              <span>
                <i className="fas fa-eye me-1"></i>
                {article.views} views
              </span>
            </div>

            {article.tags && article.tags.length > 0 && (
              <div className="mt-3">
                {article.tags.map((tag, index) => (
                  <Badge key={index} bg="secondary" className="me-2">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Article Content */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br />') }}
                style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.8'
                }}
              />
            </Card.Body>
          </Card>

          {/* Was this helpful */}
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h5 className="mb-3">Was this article helpful?</h5>
              <div className="d-flex justify-content-center gap-3">
                <Button
                  variant={hasVoted ? 'outline-success' : 'success'}
                  disabled={hasVoted}
                  onClick={() => handleVote(true)}
                >
                  <i className="fas fa-thumbs-up me-2"></i>
                  Yes ({article.helpful})
                </Button>
                <Button
                  variant={hasVoted ? 'outline-danger' : 'danger'}
                  disabled={hasVoted}
                  onClick={() => handleVote(false)}
                >
                  <i className="fas fa-thumbs-down me-2"></i>
                  No ({article.notHelpful})
                </Button>
              </div>
              {hasVoted && (
                <p className="text-success mt-3 mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  Thank you for your feedback!
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4} xl={3}>
          {/* Still need help */}
          <Card className="border-0 bg-light mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <i className="fas fa-question-circle me-2 text-info"></i>
                Still need help?
              </h5>
              <p className="text-muted small mb-3">
                If this article didn't solve your problem, our support team is here to help!
              </p>
              <Button
                variant="primary"
                className="w-100"
                onClick={() => navigate('/tickets/new')}
              >
                <i className="fas fa-ticket-alt me-2"></i>
                Submit a Ticket
              </Button>
            </Card.Body>
          </Card>

          {/* Article Meta */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">
                <i className="fas fa-info-circle me-2 text-primary"></i>
                Article Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Author</small>
                <strong>{article.author.name}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Published</small>
                <strong>{formatDate(article.publishedAt)}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Last Updated</small>
                <strong>{formatDate(article.updatedAt)}</strong>
              </div>
              <div>
                <small className="text-muted d-block mb-1">Views</small>
                <strong>{article.views}</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

